import { ImageName } from "@mcleod/images";
import {
    Alignment, AuthToken, CompanySettings, GeneralSettings, getThemeColor, LogManager, RegisteredRoutes, UserSettings,
    VerticalAlignment
} from "@mcleod/core";
import { Button, ButtonVariant, Image, ImageType, Panel, PanelProps, ScreenStack } from "@mcleod/components";
import { MainMenu } from "../menu/MainMenu";
import { MainPage } from "../mainpage/MainPage";
import { MenuFavorites } from "../menu/MenuFavorites";

const log = LogManager.getLogger("common.pageheader.PageHeader");

export abstract class PageHeader extends Panel {
    private _panelLogo: Panel;
    private _imageLogo: Image;
    private _buttonMenu: Button;
    private _menu: MainMenu;
    private static _instance: PageHeader;
    private static _pageHeaderZIndexResetTime: number = new Date().getTime();

    protected constructor(props?: Partial<PanelProps>) {
        super({
            id: "pageHeader",
            padding: 0,
            fillRow: true,
            verticalAlign: VerticalAlignment.CENTER,
            ...props
        });
        PageHeader._instance = this;

        this.initComponents();
        this.syncSettings();
    }

    public static get instance(): PageHeader {
        return this._instance;
    }

    protected abstract initComponents(): void;

    protected get panelLogo(): Panel {
        if (this._panelLogo == null) {
            this._panelLogo = new Panel({
                id: "headerLogoContainer",
                padding: 0,
                height: 60,
                fillRow: true,
                rowBreak: false
            });

            const logo = this.companyLogo;
            if (logo != null) {
                this._imageLogo = new Image({
                    id: "imageLogo",
                    imageType: ImageType.IMG,
                    imageBytes: logo,
                    height: this.logoImageHeight,
                    width: "auto",
                });
            } else {
                this._imageLogo = new Image({
                    id: "imageLogo",
                    width: 1,
                    height: 1,
                    visible: false
                });
            }
            this._panelLogo.add(this._imageLogo);
        }
        return this._panelLogo;
    }

    protected abstract get logoImageHeight(): number

    protected get buttonMenu(): Button {
        if (this._buttonMenu == null) {
            this._buttonMenu = new Button({
                id: "buttonMenu",
                imageName: ImageName.hamburger,
                variant: ButtonVariant.round,
                imageAlign: Alignment.CENTER,
                margin: "auto 14px auto 14px",
                padding: 8,
                imageWidth: 28,
                imageHeight: 28,
                rowBreak: false
            });
            this._buttonMenu.addClickListener(() => this.displayMenu());
        }
        return this._buttonMenu;
    }

    protected syncSettings() {
        const settings = this.companySettings;
        if (settings != null) {
            this.color = settings.text_color || getThemeColor("primary.reverse");
            this.backgroundColor = settings.header_bg_color || getThemeColor("primary");
        } else {
            this.removeAll();
        }

        GeneralSettings.getSingleton().addSettingsChangeListener(() => {
            this.syncNavigation();
        });
        this.syncNavigation();
    }

    get companySettings() {
        return CompanySettings.get();
    }

    get companyLogo() {
        return this.companySettings?.header_logo
    }

    get menu(): MainMenu {
        if (this._menu == null) {
            this._menu = new MainMenu();
        }
        return this._menu;
    }

    public displayMenu() {
        this.menu.show(this.buttonMenu);
    }

    public hideMenu() {
        this.menu.hide();
    }

    public clearMenu() {
        this.hideMenu();
        this._menu = null;
        MenuFavorites.clearFavorites();
    }

    private get currentRoute(): string {
        return MainPage.instance.router._route;
    }

   public syncNavigation() {
        this.syncHeader();
        this.syncMenuButton();
    }

    protected syncHeader() {
        const mainPage = MainPage.instance;
        if (mainPage != null && mainPage.pageHeader != null) {
            const shouldShowHeader = RegisteredRoutes.hasPageHeader(this.currentRoute);
            const headerAlreadyPresent = mainPage.contains(mainPage.pageHeader);
            if (shouldShowHeader && !headerAlreadyPresent)
                mainPage.insert(mainPage.pageHeader, 0);
            else if (!shouldShowHeader && headerAlreadyPresent)
                mainPage.remove(mainPage.pageHeader);
        }
    }

    protected syncMenuButton(menuButtonPanel: Panel = this) {
        const shouldShowMenuButton: boolean = this.shouldShowMenuButton;
        const menuButtonAlreadyPresent: boolean = menuButtonPanel.contains(this.buttonMenu);
        if (shouldShowMenuButton && !menuButtonAlreadyPresent)
            menuButtonPanel.insert(this.buttonMenu, 0);
        else if (!shouldShowMenuButton && menuButtonAlreadyPresent)
            menuButtonPanel.remove(this.buttonMenu);
    }

    public get shouldShowMenuButton(): boolean {
        return AuthToken.isAuthenticated() &&
            UserSettings.get() != null &&
            RegisteredRoutes.hasMainMenu(this.currentRoute);
    }

    public overridePageHeaderZIndex(componentZIndex: number, millis: number) {
        PageHeader._pageHeaderZIndexResetTime = new Date().getTime() + millis;
        log.debug("Setting zIndex, Reset Time: %o, Provided zIndex: %o, Provided millis: %o",
            PageHeader._pageHeaderZIndexResetTime, componentZIndex, millis);
        ScreenStack.incrementZIndexes(this);
        this.style.zIndex = String(componentZIndex + 1);
    }

    public resetPageHeaderZIndex() {
        const currTime = new Date().getTime();
        log.debug("zIndex Reset Time: %o, Current Time: %o", PageHeader._pageHeaderZIndexResetTime, currTime);
        if (PageHeader._pageHeaderZIndexResetTime <= currTime) {
            log.debug("Resetting zIndex, Current Value: %o, New Value: %o", this._element.style.zIndex, this.zIndex);
            this.zIndex = this.zIndex;
        } else {
            log.debug("Not resetting zIndex, not time yet");
        }

    }
}
